import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatTimestamp, formatRelativeTime } from '../utils';
import { useFilters, useDebounce, useDataCache } from '../hooks';
import styles from './Users.module.css';

/**
 * Users Page
 * 
 * View sync activity grouped by user in a detailed list.
 * Click a user row to expand and see per-device activity inline.
 * Uses global caching for fast navigation.
 */

interface DeviceStats {
  device_code: string;
  device_name: string | null;
  firmware_version: string | null;
  total_events: number;
  upload_count: number;
  download_count: number;
  corruption_count: number;
  error_count: number;
  last_sync: string;
  first_sync: string;
}

interface AggregatedUser {
  user_email: string;
  device_count: number;
  total_events: number;
  upload_count: number;
  download_count: number;
  corruption_count: number;
  error_count: number;
  last_sync: string;
  first_sync: string;
  devices: Map<string, DeviceStats>;
}

/**
 * Format relative time for last updated
 */
function formatLastUpdated(date: Date | null): string {
  if (!date) return 'Never';
  
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  
  if (diffSec < 5) return 'Just now';
  if (diffSec < 60) return `${diffSec}s ago`;
  
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  
  const diffHours = Math.floor(diffMin / 60);
  return `${diffHours}h ago`;
}

export default function Users() {
  const navigate = useNavigate();
  const [users, setUsers] = useState<AggregatedUser[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Filters - timeRange is shared across tabs
  const { timeRange, setTimeRange } = useFilters();
  const [searchInput, setSearchInput] = useState('');
  
  // Debounce search for smooth typing
  const debouncedSearch = useDebounce(searchInput, 300);
  
  // Global cache
  const { 
    fetchAllEvents, 
    getCachedAllEvents,
    hasAllEventsCache,
    isLoading: cacheLoading, 
    loadingMessage,
    error: cacheError,
    getLastFetchTime,
  } = useDataCache();

  const lastFetchTime = getLastFetchTime('allEvents');
  const hasCachedData = hasAllEventsCache(timeRange);

  // Aggregate events into users with per-device breakdown
  const aggregateUsers = useCallback((events: { user_email?: string | null; device_code?: string | null; device_name?: string | null; firmware_version?: string | null; event_type?: string; has_suspicious_chars?: boolean; success?: boolean; timestamp: string }[]) => {
    setIsProcessing(true);
    
    const userMap = new Map<string, AggregatedUser>();
    
    for (const event of events) {
      if (!event.user_email) continue;
      
      const existing = userMap.get(event.user_email);
      if (existing) {
        existing.total_events++;
        if (event.event_type === 'file_upload') existing.upload_count++;
        if (event.event_type === 'file_download') existing.download_count++;
        if (event.has_suspicious_chars) existing.corruption_count++;
        if (!event.success) existing.error_count++;
        if (new Date(event.timestamp) > new Date(existing.last_sync)) {
          existing.last_sync = event.timestamp;
        }
        if (new Date(event.timestamp) < new Date(existing.first_sync)) {
          existing.first_sync = event.timestamp;
        }
        if (event.device_code) {
          const dev = existing.devices.get(event.device_code);
          if (dev) {
            dev.total_events++;
            if (event.event_type === 'file_upload') dev.upload_count++;
            if (event.event_type === 'file_download') dev.download_count++;
            if (event.has_suspicious_chars) dev.corruption_count++;
            if (!event.success) dev.error_count++;
            if (new Date(event.timestamp) > new Date(dev.last_sync)) {
              dev.last_sync = event.timestamp;
              if (event.device_name) dev.device_name = event.device_name;
              if (event.firmware_version) dev.firmware_version = event.firmware_version;
            }
            if (new Date(event.timestamp) < new Date(dev.first_sync)) {
              dev.first_sync = event.timestamp;
            }
          } else {
            existing.devices.set(event.device_code, {
              device_code: event.device_code,
              device_name: event.device_name || null,
              firmware_version: event.firmware_version || null,
              total_events: 1,
              upload_count: event.event_type === 'file_upload' ? 1 : 0,
              download_count: event.event_type === 'file_download' ? 1 : 0,
              corruption_count: event.has_suspicious_chars ? 1 : 0,
              error_count: !event.success ? 1 : 0,
              last_sync: event.timestamp,
              first_sync: event.timestamp,
            });
          }
          existing.device_count = existing.devices.size;
        }
      } else {
        const deviceMap = new Map<string, DeviceStats>();
        if (event.device_code) {
          deviceMap.set(event.device_code, {
            device_code: event.device_code,
            device_name: event.device_name || null,
            firmware_version: event.firmware_version || null,
            total_events: 1,
            upload_count: event.event_type === 'file_upload' ? 1 : 0,
            download_count: event.event_type === 'file_download' ? 1 : 0,
            corruption_count: event.has_suspicious_chars ? 1 : 0,
            error_count: !event.success ? 1 : 0,
            last_sync: event.timestamp,
            first_sync: event.timestamp,
          });
        }
        userMap.set(event.user_email, {
          user_email: event.user_email,
          device_count: event.device_code ? 1 : 0,
          total_events: 1,
          upload_count: event.event_type === 'file_upload' ? 1 : 0,
          download_count: event.event_type === 'file_download' ? 1 : 0,
          corruption_count: event.has_suspicious_chars ? 1 : 0,
          error_count: !event.success ? 1 : 0,
          last_sync: event.timestamp,
          first_sync: event.timestamp,
          devices: deviceMap,
        });
      }
    }

    const userList = Array.from(userMap.values());
    userList.sort((a, b) => new Date(b.last_sync).getTime() - new Date(a.last_sync).getTime());
    
    setUsers(userList);
    setIsProcessing(false);
  }, []);

  // Load data (from cache or fetch)
  const loadData = useCallback(async (forceRefresh = false) => {
    const events = await fetchAllEvents(timeRange, forceRefresh);
    aggregateUsers(events);
  }, [timeRange, fetchAllEvents, aggregateUsers]);

  // Initial load - use cache if available
  useEffect(() => {
    const cached = getCachedAllEvents(timeRange);
    if (cached) {
      aggregateUsers(cached);
    } else {
      loadData();
    }
  }, [timeRange, getCachedAllEvents, aggregateUsers, loadData]);

  // Handle manual refresh
  const handleRefresh = useCallback(() => {
    loadData(true);
  }, [loadData]);

  // Filter users by search (client-side, instant)
  const filteredUsers = useMemo(() => {
    if (!debouncedSearch.trim()) return users;
    
    const searchLower = debouncedSearch.toLowerCase();
    return users.filter(u => 
      u.user_email.toLowerCase().includes(searchLower)
    );
  }, [users, debouncedSearch]);

  const [expandedUser, setExpandedUser] = useState<string | null>(null);

  const handleUserClick = (userEmail: string) => {
    setExpandedUser(prev => prev === userEmail ? null : userEmail);
  };

  const handleDeviceClick = (deviceCode: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/events?device_code=${encodeURIComponent(deviceCode)}`);
  };

  const handleViewAllEvents = (userEmail: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/events?user_email=${encodeURIComponent(userEmail)}`);
  };

  const getDeviceHealth = (device: DeviceStats) => {
    if (device.total_events === 0) return 'healthy';
    const corruptionRate = device.corruption_count / device.total_events;
    if (corruptionRate > 0.1) return 'critical';
    if (corruptionRate > 0.05) return 'warning';
    if (device.corruption_count > 0) return 'minor';
    return 'healthy';
  };

  const handleClearSearch = () => {
    setSearchInput('');
  };

  const isLoading = cacheLoading || isProcessing;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Users</h1>
          <p className={styles.subtitle}>
            {filteredUsers.length.toLocaleString()} users with activity
            {hasCachedData && !cacheLoading && <span className={styles.cachedBadge}>Cached</span>}
          </p>
        </div>
        <div className={styles.headerActions}>
          <span className={styles.lastUpdated}>
            Updated {formatLastUpdated(lastFetchTime)}
          </span>
          <button 
            className={styles.refreshButton} 
            onClick={handleRefresh}
            disabled={isLoading}
            title="Refresh data"
          >
            <span className={`${styles.refreshIcon} ${isLoading ? styles.spinning : ''}`}>↻</span>
            Refresh
          </button>
        </div>
      </div>

      <div className={styles.filters}>
        <div className={styles.searchWrapper}>
          <input
            type="text"
            placeholder="Search by email..."
            className={styles.searchInput}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          {searchInput && (
            <button 
              className={styles.clearSearchButton}
              onClick={handleClearSearch}
              title="Clear search"
            >
              ✕
            </button>
          )}
        </div>
        <select 
          className={styles.select}
          value={timeRange}
          onChange={(e) => setTimeRange(e.target.value)}
        >
          <option value="">All Time</option>
          <option value="1h">Last Hour</option>
          <option value="24h">Last 24 Hours</option>
          <option value="7d">Last 7 Days</option>
          <option value="30d">Last 30 Days</option>
        </select>
      </div>

      {cacheError && (
        <div className={styles.error}>
          {cacheError}
          <button className={styles.retryButton} onClick={handleRefresh}>
            Retry
          </button>
        </div>
      )}

      <div className={styles.tableContainer}>
        {isLoading && users.length === 0 ? (
          <div className={styles.loading}>{loadingMessage || 'Loading users...'}</div>
        ) : (
          <table className={`${styles.table} ${isLoading ? styles.tableLoading : ''}`}>
            <thead>
              <tr>
                <th>User Email</th>
                <th>Devices</th>
                <th>Events</th>
                <th>Uploads</th>
                <th>Downloads</th>
                <th>Corrupted</th>
                <th>Errors</th>
                <th>Last Activity</th>
                <th>First Activity</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={9} className={styles.emptyRow}>
                    {searchInput ? 'No users match your search.' : 'No users found in the selected time range.'}
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => {
                  const isExpanded = expandedUser === user.user_email;
                  const deviceList = Array.from(user.devices.values())
                    .sort((a, b) => new Date(b.last_sync).getTime() - new Date(a.last_sync).getTime());
                  return (
                    <React.Fragment key={user.user_email}>
                      <tr 
                        className={`${styles.clickableRow} ${isExpanded ? styles.expandedRow : ''}`}
                        onClick={() => handleUserClick(user.user_email)}
                      >
                        <td className={styles.emailCell}>
                          <span className={styles.expandArrow}>{isExpanded ? '▼' : '▶'}</span>
                          <span className={styles.email}>{user.user_email}</span>
                        </td>
                        <td className={styles.numCell}>{user.device_count}</td>
                        <td className={styles.numCell}>{user.total_events.toLocaleString()}</td>
                        <td className={styles.numCell}>{user.upload_count.toLocaleString()}</td>
                        <td className={styles.numCell}>{user.download_count.toLocaleString()}</td>
                        <td className={styles.numCell}>
                          {user.corruption_count > 0 ? (
                            <span className={styles.corruptedBadge}>⚠️ {user.corruption_count}</span>
                          ) : (
                            <span className={styles.okValue}>0</span>
                          )}
                        </td>
                        <td className={styles.numCell}>
                          {user.error_count > 0 ? (
                            <span className={styles.errorBadge}>{user.error_count}</span>
                          ) : (
                            <span className={styles.okValue}>0</span>
                          )}
                        </td>
                        <td>
                          <div className={styles.timestamp}>
                            <span className={styles.timestampFull}>{formatTimestamp(user.last_sync)}</span>
                            <span className={styles.timestampRelative}>{formatRelativeTime(user.last_sync)}</span>
                          </div>
                        </td>
                        <td>
                          <div className={styles.timestamp}>
                            <span className={styles.timestampFull}>{formatTimestamp(user.first_sync)}</span>
                          </div>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className={styles.devicePanelRow}>
                          <td colSpan={9} className={styles.devicePanelCell}>
                            <div className={styles.devicePanel}>
                              <div className={styles.devicePanelHeader}>
                                <span className={styles.devicePanelTitle}>
                                  Devices ({deviceList.length})
                                </span>
                                <button
                                  className={styles.viewAllEventsButton}
                                  onClick={(e) => handleViewAllEvents(user.user_email, e)}
                                >
                                  View all events →
                                </button>
                              </div>
                              <table className={styles.deviceTable}>
                                <thead>
                                  <tr>
                                    <th>Status</th>
                                    <th>Device Code</th>
                                    <th>Device Name</th>
                                    <th>Firmware</th>
                                    <th>Events</th>
                                    <th>Uploads</th>
                                    <th>Downloads</th>
                                    <th>Corrupted</th>
                                    <th>Errors</th>
                                    <th>Last Activity</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {deviceList.map((device) => {
                                    const health = getDeviceHealth(device);
                                    return (
                                      <tr
                                        key={device.device_code}
                                        className={styles.deviceRow}
                                        onClick={(e) => handleDeviceClick(device.device_code, e)}
                                      >
                                        <td className={styles.deviceStatusCell}>
                                          <span className={`${styles.healthBadge} ${styles[`badge_${health}`]}`}>
                                            {health === 'healthy' && '✓'}
                                            {health === 'minor' && '!'}
                                            {health === 'warning' && '⚠'}
                                            {health === 'critical' && '🔴'}
                                          </span>
                                        </td>
                                        <td>
                                          <span className={styles.deviceCode}>{device.device_code}</span>
                                        </td>
                                        <td className={styles.deviceNameCell}>
                                          {device.device_name || <span className={styles.mutedText}>—</span>}
                                        </td>
                                        <td>
                                          <span className={styles.firmware}>{device.firmware_version || '—'}</span>
                                        </td>
                                        <td className={styles.numCell}>{device.total_events.toLocaleString()}</td>
                                        <td className={styles.numCell}>{device.upload_count.toLocaleString()}</td>
                                        <td className={styles.numCell}>{device.download_count.toLocaleString()}</td>
                                        <td className={styles.numCell}>
                                          {device.corruption_count > 0 ? (
                                            <span className={styles.corruptedBadge}>⚠️ {device.corruption_count}</span>
                                          ) : (
                                            <span className={styles.okValue}>0</span>
                                          )}
                                        </td>
                                        <td className={styles.numCell}>
                                          {device.error_count > 0 ? (
                                            <span className={styles.errorBadge}>{device.error_count}</span>
                                          ) : (
                                            <span className={styles.okValue}>0</span>
                                          )}
                                        </td>
                                        <td>
                                          <div className={styles.timestamp}>
                                            <span className={styles.timestampFull}>{formatTimestamp(device.last_sync)}</span>
                                            <span className={styles.timestampRelative}>{formatRelativeTime(device.last_sync)}</span>
                                          </div>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
